import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  min?: string;
  max?: string;
  error?: string;
  className?: string;
}

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function parseYMD(s: string): { y: number; m: number; d: number } | null {
  const parts = s.split('-');
  if (parts.length !== 3) return null;
  const y = parseInt(parts[0]!, 10);
  const m = parseInt(parts[1]!, 10);
  const d = parseInt(parts[2]!, 10);
  if (isNaN(y) || isNaN(m) || isNaN(d)) return null;
  return { y, m, d };
}

function toYMD(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function formatDisplay(s: string): string {
  const p = parseYMD(s);
  if (!p) return '';
  const monthShort = MONTHS[p.m - 1]?.slice(0, 3) ?? '';
  return `${monthShort} ${p.d}, ${p.y}`;
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

/** 0=Mon … 6=Sun (ISO weekday) */
function startDayOfMonth(year: number, month: number): number {
  const d = new Date(year, month - 1, 1).getDay(); // 0=Sun
  return d === 0 ? 6 : d - 1;
}

export function DatePicker({
  value,
  onChange,
  label,
  placeholder = 'Select date',
  min,
  max,
  error,
  className = '',
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(() => {
    const p = parseYMD(value);
    return p ? p.y : new Date().getFullYear();
  });
  const [viewMonth, setViewMonth] = useState(() => {
    const p = parseYMD(value);
    return p ? p.m : new Date().getMonth() + 1;
  });
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Sync viewDate when value changes externally
  useEffect(() => {
    const p = parseYMD(value);
    if (p) {
      setViewYear(p.y);
      setViewMonth(p.m);
    }
  }, [value]);

  // Position the dropdown
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const popupWidth = 280;
    setPos({
      top: rect.bottom + 4,
      left: Math.min(rect.left, window.innerWidth - popupWidth - 8),
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        dropdownRef.current?.contains(target)
      )
        return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const prevMonth = () => {
    if (viewMonth === 1) {
      setViewMonth(12);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const nextMonth = () => {
    if (viewMonth === 12) {
      setViewMonth(1);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const handleSelect = (day: number, month: number, year: number) => {
    onChange(toYMD(year, month, day));
    setOpen(false);
  };

  const isDisabled = (y: number, m: number, d: number): boolean => {
    const dateStr = toYMD(y, m, d);
    if (min && dateStr < min) return true;
    if (max && dateStr > max) return true;
    return false;
  };

  const selected = parseYMD(value);
  const today = new Date();
  const todayY = today.getFullYear();
  const todayM = today.getMonth() + 1;
  const todayD = today.getDate();

  // Build calendar grid
  const totalDays = daysInMonth(viewYear, viewMonth);
  const startDay = startDayOfMonth(viewYear, viewMonth);

  // Previous month days
  const prevMonthYear = viewMonth === 1 ? viewYear - 1 : viewYear;
  const prevMonthNum = viewMonth === 1 ? 12 : viewMonth - 1;
  const prevMonthDays = daysInMonth(prevMonthYear, prevMonthNum);

  const cells: { day: number; month: number; year: number; current: boolean }[] = [];

  for (let i = startDay - 1; i >= 0; i--) {
    cells.push({ day: prevMonthDays - i, month: prevMonthNum, year: prevMonthYear, current: false });
  }
  for (let d = 1; d <= totalDays; d++) {
    cells.push({ day: d, month: viewMonth, year: viewYear, current: true });
  }
  const nextMonthYear = viewMonth === 12 ? viewYear + 1 : viewYear;
  const nextMonthNum = viewMonth === 12 ? 1 : viewMonth + 1;
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++) {
    cells.push({ day: d, month: nextMonthNum, year: nextMonthYear, current: false });
  }

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label className="block text-[10px] font-semibold text-ink-3 mb-1">
          {label}
        </label>
      )}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center gap-2 bg-surface border rounded-lg px-3 py-1.5 text-xs font-mono text-left outline-none transition-all duration-200 cursor-pointer ${
          error
            ? 'border-red'
            : open
              ? 'border-orange shadow-[0_0_8px_rgba(249,115,22,0.15)]'
              : 'border-glass-border hover:border-orange/30'
        }`}
      >
        <Calendar size={12} className="text-ink-4 flex-shrink-0" />
        <span className={value ? 'text-ink' : 'text-ink-4'}>
          {value ? formatDisplay(value) : placeholder}
        </span>
      </button>
      {error && <p className="text-[10px] text-red mt-1">{error}</p>}

      {open &&
        pos &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-[60] rounded-lg shadow-modal p-3 w-[280px] border border-glass-border"
            style={{ top: pos.top, left: pos.left, background: 'var(--popup-bg)' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-2">
              <button
                type="button"
                onClick={prevMonth}
                className="w-6 h-6 rounded-md flex items-center justify-center text-ink-3 hover:bg-surface-2 hover:text-ink transition-colors cursor-pointer"
              >
                <ChevronLeft size={14} />
              </button>
              <span className="text-[11px] font-bold text-ink">
                {MONTHS[viewMonth - 1]} {viewYear}
              </span>
              <button
                type="button"
                onClick={nextMonth}
                className="w-6 h-6 rounded-md flex items-center justify-center text-ink-3 hover:bg-surface-2 hover:text-ink transition-colors cursor-pointer"
              >
                <ChevronRight size={14} />
              </button>
            </div>

            {/* Weekday labels */}
            <div className="grid grid-cols-7 mb-1">
              {WEEKDAYS.map((wd) => (
                <div
                  key={wd}
                  className="text-center text-[9px] font-bold text-ink-4 uppercase py-1"
                >
                  {wd}
                </div>
              ))}
            </div>

            {/* Day grid */}
            <div className="grid grid-cols-7">
              {cells.map((cell, i) => {
                const isSelected =
                  selected &&
                  selected.y === cell.year &&
                  selected.m === cell.month &&
                  selected.d === cell.day;
                const isToday =
                  cell.year === todayY &&
                  cell.month === todayM &&
                  cell.day === todayD;
                const disabled = isDisabled(cell.year, cell.month, cell.day);

                return (
                  <button
                    key={i}
                    type="button"
                    disabled={disabled}
                    onClick={() => handleSelect(cell.day, cell.month, cell.year)}
                    className={`w-full aspect-square flex items-center justify-center text-[10px] font-semibold rounded-md transition-colors cursor-pointer ${
                      isSelected
                        ? 'bg-orange text-white'
                        : isToday
                          ? 'border border-orange/30 text-orange'
                          : cell.current
                            ? 'text-ink hover:bg-surface-2'
                            : 'text-ink-4 hover:bg-surface'
                    } ${disabled ? '!text-ink-4/50 !cursor-not-allowed hover:!bg-transparent' : ''}`}
                  >
                    {cell.day}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
